import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Download, Loader2, Building2, MessageSquare, BarChart3, Heart, Eye, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import SlidePreviews from "@/components/proposal/SlidePreviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProposalEditor = ({ open, onClose, getAuthHeaders, consultantName, consultantFunction, consultantNif, consultantEmail, formData, results }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [activeSection, setActiveSection] = useState("texts");
  const [candidateEmail, setCandidateEmail] = useState(consultantEmail || "");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [proposalLink, setProposalLink] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Auto-preencher email do candidato sempre que muda no formulário do simulador
  useEffect(() => {
    if (consultantEmail) setCandidateEmail(consultantEmail);
  }, [consultantEmail]);

  const firstName = consultantName?.split(" ")[0] || "";
  const nameParts = (consultantName || "").trim().split(/\s+/);
  const fullDisplayName = nameParts.length >= 2 ? `${nameParts[0]} ${nameParts[nameParts.length - 1]}` : consultantName || "";

  const [texts, setTexts] = useState({
    cover_title: "Proposta de Trabalho",
    cover_subtitle: fullDisplayName,
    greeting_text: `Olá ${firstName},`,
    motivational_text: "Muitos parabéns! Estamos entusiasmados com a possibilidade de contarmos contigo na nossa equipa. Acreditamos que o teu talento e dedicação serão uma mais-valia para os nossos projetos. Preparámos esta proposta financeira especialmente para ti.",
    motivational_text_freelancer: "",
    annual_title: "Visão Anual Detalhada",
    annual_subtitle: "Projeção de 12 meses com todos os benefícios e descontos",
    thanks_title: "Obrigado!",
    thanks_text: "Estamos disponíveis para esclarecer qualquer dúvida. Aguardamos a tua resposta com entusiasmo!",
    other_benefits_title: "Benefícios",
    other_benefits_text: "",
    other_benefits_text_freelancer: "",
    other_benefits_text_b2b: "",
    freelancer_payment_months: 11,
    b2b_payment_months: 11,
    footer_text: "© {ano} Evolutt - Unlocking What's Next",
    health_insurance_name: "",
    motivational_alignment: "justify",
    thanks_alignment: "justify",
  });

  const regimeType = formData?.regime_type || "contract";
  const isFreelancer = regimeType === "green_receipts" || regimeType === "b2b";
  const isPerm = (formData?.business_type === "perm");

  const [company, setCompany] = useState({
    company_name: "Evolutt",
    company_slogan: "Unlocking What's Next",
    company_address: "",
    company_postal_code: "",
    company_city: "Portugal",
    company_phone1: "",
    company_phone2: "",
    company_website: "",
    company_email: "",
  });

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  useEffect(() => {
    if (consultantName) {
      const parts = consultantName.trim().split(/\s+/);
      const first = parts[0] || "";
      const display = parts.length >= 2 ? `${parts[0]} ${parts[parts.length - 1]}` : consultantName;
      setTexts(prev => ({
        ...prev,
        greeting_text: `Olá ${first},`,
        cover_subtitle: display,
      }));
    }
  }, [consultantName]);

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const res = await axios.get(`${API}/proposal-settings`, { headers: getAuthHeaders() });
      if (res.data?.company) setCompany(prev => ({ ...prev, ...res.data.company }));
      if (res.data?.texts) {
        const t = res.data.texts;
        const parts = (consultantName || "").trim().split(/\s+/);
        const first = parts[0] || "";
        const display = parts.length >= 2 ? `${parts[0]} ${parts[parts.length - 1]}` : consultantName || "";
        setTexts(prev => ({
          ...prev,
          cover_title: t.cover_title || prev.cover_title,
          cover_subtitle: display || t.cover_subtitle || "",
          greeting_text: first ? `Olá ${first},` : (t.greeting_text || prev.greeting_text),
          motivational_text: t.motivational_text || prev.motivational_text,
          motivational_text_freelancer: t.motivational_text_freelancer != null ? t.motivational_text_freelancer : prev.motivational_text_freelancer,
          motivational_alignment: t.motivational_alignment || prev.motivational_alignment,
          annual_title: t.annual_title || prev.annual_title,
          annual_subtitle: t.annual_subtitle || prev.annual_subtitle,
          thanks_title: t.thanks_title || prev.thanks_title,
          thanks_text: t.thanks_text || prev.thanks_text,
          thanks_alignment: t.thanks_alignment || prev.thanks_alignment,
          other_benefits_title: t.other_benefits_title || prev.other_benefits_title,
          other_benefits_text: t.other_benefits_text != null ? t.other_benefits_text : prev.other_benefits_text,
          other_benefits_text_freelancer: t.other_benefits_text_freelancer != null ? t.other_benefits_text_freelancer : prev.other_benefits_text_freelancer,
          freelancer_payment_months: t.freelancer_payment_months != null ? t.freelancer_payment_months : prev.freelancer_payment_months,
          footer_text: t.footer_text || prev.footer_text,
          health_insurance_name: t.health_insurance_name || prev.health_insurance_name,
        }));
      }
    } catch (err) {
      console.error("Error loading proposal settings:", err);
    } finally {
      setLoadingSettings(false);
    }
  };

  const saveMySettings = async ({ silent = false } = {}) => {
    try {
      await axios.post(`${API}/proposal-settings`, {
        company,
        texts: {
          cover_title: texts.cover_title,
          cover_subtitle: texts.cover_subtitle,
          greeting_template: texts.greeting_text.replace(firstName, "{primeiro_nome}"),
          motivational_text: texts.motivational_text,
          motivational_text_freelancer: texts.motivational_text_freelancer,
          motivational_alignment: texts.motivational_alignment,
          annual_title: texts.annual_title,
          annual_subtitle: texts.annual_subtitle,
          thanks_title: texts.thanks_title,
          thanks_text: texts.thanks_text,
          thanks_alignment: texts.thanks_alignment,
          other_benefits_title: texts.other_benefits_title,
          other_benefits_text: texts.other_benefits_text,
          other_benefits_text_freelancer: texts.other_benefits_text_freelancer,
          freelancer_payment_months: parseInt(texts.freelancer_payment_months || 11),
          footer_text: texts.footer_text,
        },
      }, { headers: getAuthHeaders() });
      if (!silent) toast.success("Definições da proposta guardadas");
    } catch (err) {
      if (!silent) toast.error("Erro ao guardar definições");
    }
  };

  const generatePDF = async () => {
    if (!results) {
      toast.error("Calcule a simulação primeiro");
      return;
    }
    setLoading(true);
    try {
      // Escolhe motivational text consoante regime (se freelancer e existe versão específica, usa essa)
      const _motivText = isFreelancer && (texts.motivational_text_freelancer || "").trim()
        ? texts.motivational_text_freelancer
        : texts.motivational_text;

      const payload = {
        consultant_name: consultantName || "Consultor",
        consultant_nif: consultantNif || "",
        consultant_function: consultantFunction || "",
        ...texts,
        motivational_text: _motivText,
        ...company,
        // Creator info — slide final do PDF
        creator_name: user?.name || user?.email || "",
        creator_email: user?.email || "",
        creator_phone: user?.phone || "",
        creator_job_function: user?.job_function || "",
        simulation_data: { ...(formData || {}), consultant_function: consultantFunction || formData?.consultant_function || "" },
        results: results || {},
      };

      const response = await axios.post(`${API}/proposal/generate-pdf`, payload, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        responseType: "blob",
      });

      // Persistir os textos automaticamente quando o PDF é gerado, para que
      // da próxima vez que o utilizador abrir o editor os seus textos voltem a
      // aparecer (em vez de cair nos defaults).
      try { await saveMySettings({ silent: true }); } catch { /* non-critical */ }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Proposta_${firstName || "consultor"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF gerado com sucesso!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Erro ao gerar PDF");
    } finally {
      setLoading(false);
    }
  };

  const generateSigningLink = async () => {
    if (!results) {
      toast.error("Calcule a simulação primeiro");
      return;
    }
    setGeneratingLink(true);
    try {
      const payload = {
        candidate_name: consultantName || "Candidato",
        candidate_email: candidateEmail,
        candidate_nif: consultantNif || formData?.consultant_nif || "",
        candidate_function: consultantFunction || "",
        simulation_data: { ...(formData || {}), consultant_function: consultantFunction || formData?.consultant_function || "" },
        results: results || {},
        proposal_settings: { texts, company },
      };

      const res = await axios.post(`${API}/proposals`, payload, {
        headers: getAuthHeaders(),
      });

      const link = `${window.location.origin}/proposal/sign/${res.data.token}`;
      setProposalLink(link);
      try { await navigator.clipboard.writeText(link); } catch { /* clipboard blocked */ }
      toast.success("Link de assinatura criado!");
      return { proposalId: res.data.id, token: res.data.token, link };
    } catch (err) {
      toast.error("Erro ao criar proposta");
      return null;
    } finally {
      setGeneratingLink(false);
    }
  };

  const sendByEmail = async () => {
    if (!candidateEmail || !candidateEmail.includes("@")) {
      toast.error("Indique o email do candidato");
      return;
    }
    if (!results) { toast.error("Calcule a simulação primeiro"); return; }
    setSendingEmail(true);
    try {
      // 1) Garantir que existe link de proposta — se ainda não, criar
      let link = proposalLink;
      if (!link) {
        const created = await generateSigningLink();
        if (!created) return;
        link = created.link;
      }

      // 2) Buscar template do email
      let tpl = { subject: "Proposta de Colaboração - {primeiro_nome}", body_text: "" };
      try {
        const r = await axios.get(`${API}/email-templates/proposal`, { headers: getAuthHeaders() });
        if (r.data) tpl = r.data;
      } catch { /* fallback to defaults */ }

      // 3) Preencher placeholders
      const _firstName = (consultantName || "Olá").trim().split(/\s+/)[0];
      const ctx = {
        "{primeiro_nome}": _firstName,
        "{empresa}": company.company_name || "Evolutt",
        "{link}": link,
        "{remetente_nome}": company.company_name || "Evolutt",
        "{remetente_email}": company.company_email || "",
        "{remetente_phone_line}": company.company_phone1 ? `\n${company.company_phone1}` : "",
      };
      const subject = Object.entries(ctx).reduce((acc, [k, v]) => acc.replaceAll(k, v), tpl.subject || "");
      const body = Object.entries(ctx).reduce((acc, [k, v]) => acc.replaceAll(k, v), tpl.body_text || `Olá ${_firstName},\n\nPartilho a proposta de colaboração.\n\nLink: ${link}\n\nCumprimentos.`);

      // 4) Gerar PDF e fazer download (para anexar manualmente)
      await generatePDF();

      // 5) Abrir mailto
      const mailto = `mailto:${encodeURIComponent(candidateEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      toast.success("Email aberto no teu cliente. PDF descarregado — anexa-o ao email antes de enviar.");
    } catch (err) {
      toast.error("Erro ao preparar email");
    } finally {
      setSendingEmail(false);
    }
  };

  const result = results?.results?.[0];

  // (LOGO_DARK / LOGO_LIGHT moved to SlidePreviews.js where they're used)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-manrope font-bold text-xl flex items-center gap-2">
            <FileText className="w-5 h-5 text-[var(--brand-primary)]" />
            Gerar Proposta PDF
          </DialogTitle>
        </DialogHeader>

        {loadingSettings ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-primary)]" />
            <span className="ml-2 text-sm text-muted-foreground">A carregar definições...</span>
          </div>
        ) : (
          <>
            <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
              <TabsList className="grid grid-cols-3 mb-4">
                <TabsTrigger value="texts" className="flex items-center gap-1.5 text-xs">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Textos
                </TabsTrigger>
                <TabsTrigger value="company" className="flex items-center gap-1.5 text-xs">
                  <Building2 className="w-3.5 h-3.5" />
                  Empresa
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex items-center gap-1.5 text-xs">
                  <Eye className="w-3.5 h-3.5" />
                  Pré-visualização
                </TabsTrigger>
              </TabsList>

              {/* TEXTS TAB */}
              <TabsContent value="texts" className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Título da Capa</Label>
                    <Input value={texts.cover_title} onChange={e => setTexts({...texts, cover_title: e.target.value})} className="mt-1" data-testid="proposal-cover-title" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Subtítulo da Capa</Label>
                    <Input value={texts.cover_subtitle} onChange={e => setTexts({...texts, cover_subtitle: e.target.value})} className="mt-1" placeholder="Opcional" />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-[var(--brand-primary)]/5 border border-[var(--brand-primary)]/10">
                  <Label className="text-xs font-semibold text-[var(--brand-primary)]">Slide 2 - Saudação</Label>
                  <Input value={texts.greeting_text} onChange={e => setTexts({...texts, greeting_text: e.target.value})} className="mt-1.5" data-testid="proposal-greeting" />
                  <div className="flex items-center justify-between mt-2">
                    <Label className="text-xs font-medium">Texto Motivacional</Label>
                    <div className="inline-flex rounded-md border border-[var(--brand-primary)]/20 overflow-hidden" data-testid="motivational-align-toggle">
                      {[
                        { v: "left", label: "Esquerda" },
                        { v: "center", label: "Centro" },
                        { v: "justify", label: "Justificado" },
                      ].map(opt => (
                        <button
                          type="button"
                          key={opt.v}
                          onClick={() => setTexts({ ...texts, motivational_alignment: opt.v })}
                          className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${texts.motivational_alignment === opt.v ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10"}`}
                          data-testid={`motivational-align-${opt.v}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea value={texts.motivational_text} onChange={e => setTexts({...texts, motivational_text: e.target.value})} className="mt-1 min-h-[80px]" data-testid="proposal-motivational" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Título Visão Anual</Label>
                    <Input value={texts.annual_title} onChange={e => setTexts({...texts, annual_title: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Subtítulo Anual</Label>
                    <Input value={texts.annual_subtitle} onChange={e => setTexts({...texts, annual_subtitle: e.target.value})} className="mt-1" />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-[#FADCBC]/30 border border-[#F09678]/20" style={{ display: isPerm ? "none" : undefined }} data-testid="proposal-benefits-block">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-[var(--brand-primary)]">Slide 4 - Benefícios</Label>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-primary)] text-white">
                      {isFreelancer ? (regimeType === "b2b" ? "B2B" : "Recibos Verdes") : "Contrato"}
                    </span>
                  </div>
                  <Input value={texts.other_benefits_title} onChange={e => setTexts({...texts, other_benefits_title: e.target.value})} className="mt-1.5" placeholder="Título do slide" data-testid="proposal-other-benefits-title" />

                  {isFreelancer && (
                    <div className="mt-2 flex items-center gap-2">
                      <Label className="text-xs font-medium">Meses de Faturação</Label>
                      <div className="inline-flex rounded-md border border-[var(--brand-primary)]/20 overflow-hidden" data-testid="freelancer-months-toggle">
                        {[11, 12].map(m => (
                          <button
                            type="button"
                            key={m}
                            onClick={() => setTexts({ ...texts, freelancer_payment_months: m })}
                            className={`px-3 py-0.5 text-[11px] font-medium transition-colors ${parseInt(texts.freelancer_payment_months) === m ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10"}`}
                            data-testid={`freelancer-months-${m}`}
                          >
                            {m} meses
                          </button>
                        ))}
                      </div>
                      <span className="text-[10px] text-muted-foreground">(apresentado na tabela do PDF)</span>
                    </div>
                  )}

                  <Label className="text-xs font-medium mt-2 block">
                    Texto {isFreelancer ? "(para B2B / Recibos Verdes)" : "(para Contrato)"} — use &quot;- &quot; para bullets, **negrito** entre asteriscos
                  </Label>
                  {isFreelancer ? (
                    <Textarea
                      value={texts.other_benefits_text_freelancer}
                      onChange={e => setTexts({...texts, other_benefits_text_freelancer: e.target.value})}
                      className="mt-1 min-h-[180px] text-xs"
                      placeholder="Texto para B2B/Recibos Verdes..."
                      data-testid="proposal-other-benefits-text-freelancer"
                    />
                  ) : (
                    <Textarea
                      value={texts.other_benefits_text}
                      onChange={e => setTexts({...texts, other_benefits_text: e.target.value})}
                      className="mt-1 min-h-[180px] text-xs"
                      placeholder="Texto para Contrato..."
                      data-testid="proposal-other-benefits-text"
                    />
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Texto justificado automaticamente. Para o outro regime, muda o regime no simulador.
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-[#F09678]/10 border border-[#F09678]/20">
                  <Label className="text-xs font-semibold text-[var(--brand-primary)]">Slide 5 - Agradecimento</Label>
                  <Input value={texts.thanks_title} onChange={e => setTexts({...texts, thanks_title: e.target.value})} className="mt-1.5" data-testid="proposal-thanks-title" />
                  <div className="flex items-center justify-between mt-2">
                    <Label className="text-xs font-medium">Texto de Agradecimento</Label>
                    <div className="inline-flex rounded-md border border-[var(--brand-primary)]/20 overflow-hidden" data-testid="thanks-align-toggle">
                      {[
                        { v: "left", label: "Esquerda" },
                        { v: "center", label: "Centro" },
                        { v: "justify", label: "Justificado" },
                      ].map(opt => (
                        <button
                          type="button"
                          key={opt.v}
                          onClick={() => setTexts({ ...texts, thanks_alignment: opt.v })}
                          className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${texts.thanks_alignment === opt.v ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10"}`}
                          data-testid={`thanks-align-${opt.v}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea value={texts.thanks_text} onChange={e => setTexts({...texts, thanks_text: e.target.value})} className="mt-1 min-h-[60px]" data-testid="proposal-thanks-text" />
                </div>

                <div>
                  <Label className="text-xs font-medium">Rodapé (use {"{ano}"} para o ano atual)</Label>
                  <Input value={texts.footer_text} onChange={e => setTexts({...texts, footer_text: e.target.value})} className="mt-1" />
                </div>

                {/* "Seguro de Saúde (nota no PDF)" REMOVIDO a pedido do utilizador */}
              </TabsContent>

              {/* COMPANY TAB */}
              <TabsContent value="company" className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Nome da Empresa</Label>
                    <Input value={company.company_name} onChange={e => setCompany({...company, company_name: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Slogan</Label>
                    <Input value={company.company_slogan} onChange={e => setCompany({...company, company_slogan: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium">Morada</Label>
                  <Input value={company.company_address} onChange={e => setCompany({...company, company_address: e.target.value})} className="mt-1" placeholder="Ex: Rua das Flores, 123" data-testid="proposal-company-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Código Postal</Label>
                    <Input value={company.company_postal_code} onChange={e => setCompany({...company, company_postal_code: e.target.value})} className="mt-1" placeholder="1000-000" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Cidade</Label>
                    <Input value={company.company_city} onChange={e => setCompany({...company, company_city: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Telefone 1</Label>
                    <Input value={company.company_phone1} onChange={e => setCompany({...company, company_phone1: e.target.value})} className="mt-1" placeholder="+351 000 000 000" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Telefone 2</Label>
                    <Input value={company.company_phone2} onChange={e => setCompany({...company, company_phone2: e.target.value})} className="mt-1" placeholder="+351 000 000 000" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">Website</Label>
                    <Input value={company.company_website} onChange={e => setCompany({...company, company_website: e.target.value})} className="mt-1" placeholder="www.evollutt.pt" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Email</Label>
                    <Input value={company.company_email} onChange={e => setCompany({...company, company_email: e.target.value})} className="mt-1" placeholder="info@evollutt.pt" />
                  </div>
                </div>

                <Button variant="outline" size="sm" className="w-full mt-2" onClick={saveMySettings}>
                  Guardar como minhas definições padrão
                </Button>
              </TabsContent>

              {/* PREVIEW TAB */}
              <TabsContent value="preview" className="space-y-3">
                <SlidePreviews texts={texts} company={company} result={result} />
              </TabsContent>
            </Tabs>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4 border-t">
              {/* Email + Link de Assinatura */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground mb-1 block">Email do Candidato (para link de assinatura)</Label>
                  <Input
                    type="email"
                    value={candidateEmail}
                    onChange={(e) => setCandidateEmail(e.target.value)}
                    placeholder="candidato@email.com"
                    className="h-9"
                    data-testid="candidate-email-input"
                  />
                </div>
                <Button
                  onClick={generateSigningLink}
                  disabled={generatingLink || !results}
                  variant="outline"
                  className="h-9 shrink-0 border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white"
                  data-testid="generate-link-btn"
                >
                  {generatingLink ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  Gerar Link
                </Button>
                <Button
                  onClick={sendByEmail}
                  disabled={sendingEmail || !results || !candidateEmail}
                  className="h-9 shrink-0 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)]/90"
                  data-testid="send-email-btn"
                  title="Abre o teu cliente de email com o link e descarrega o PDF para anexares"
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Criar Email
                </Button>
              </div>

              {/* Show generated link */}
              {proposalLink && (
                <div className="flex items-center gap-2 p-2 bg-brand-50 rounded-lg border border-brand-200">
                  <input
                    readOnly
                    value={proposalLink}
                    className="flex-1 bg-transparent text-xs font-mono text-brand-800 outline-none"
                    data-testid="proposal-link-display"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-7 text-xs border-brand-400 text-brand-700 hover:bg-brand-100"
                    onClick={() => { try { navigator.clipboard.writeText(proposalLink); toast.success("Link copiado!"); } catch { /* clipboard blocked */ } }}
                    data-testid="copy-link-btn"
                  >
                    Copiar
                  </Button>
                </div>
              )}

              {/* PDF + Save buttons */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={saveMySettings} className="flex-1" data-testid="save-proposal-settings">
                  Guardar Definições
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="reset-proposal-settings"
                  onClick={async () => {
                    if (!window.confirm("Repor os textos do PDF aos definidos no Backoffice? Vai perder customizações pessoais.")) return;
                    try {
                      await axios.delete(`${API}/proposal-settings`, { headers: getAuthHeaders() });
                      toast.success("Reposto — a recarregar...");
                      await loadSettings();
                    } catch {
                      toast.error("Erro ao repor definições");
                    }
                  }}
                >
                  Usar Padrão Backoffice
                </Button>
                <Button
                  onClick={generatePDF}
                  disabled={loading || !results}
                  className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white"
                  data-testid="generate-pdf-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A gerar...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" />Gerar PDF</>
                  )}
                </Button>
              </div>
            </div>

            {!results && (
              <p className="text-xs text-center text-amber-600 bg-amber-50 p-2 rounded">
                Execute a simulação primeiro para poder gerar o PDF.
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProposalEditor;
